// Explorer > project > Sellers: the talent SCOUT shortlisted, per search.
//
// The sibling table on this tab (`ProjectSellers`) shows the concierge OUTREACH:
// who was messaged, who replied, who quoted. Threads exist only under a concierge
// run, so a client who ran the self-serve search was never contacted by anybody
// and that table was empty for them — which read as "nothing happened here" when
// SCOUT had in fact ranked a full shortlist and the carousel had shown it.
//
// This is that shortlist, for BOTH surfaces (one `scout_searches` row per search,
// `surface` says which product it served). It is deliberately COMPACTER than the
// Talent console's Searches page: this answers "who did we find, and were they
// shown to the client", and the per-pick forensics (the four fit grades, the gig
// chosen, the quality bucket) stay one click away on that page rather than
// widening this tab to fifteen columns. Same data either way — the backend runs
// both through one adapter.
//
// Nothing here computes a rate or an average. Every number is a property of the
// one record being shown, which is the line `lib/seller-threads.ts` states in
// full and the reason this tab may read live product state at all.
import { Badge, TableExport } from "@recruiter/shared";
import type { AdminSearchPick } from "@recruiter/shared/types";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import type { ProjectScoutSearch } from "../lib/api";
import { hasArea } from "../lib/areas";
import { formatDateTime } from "../lib/format";
import { myAreasQuery } from "../lib/queries";
import { funnelLadderText, missingGigData } from "../lib/search-funnel";
import { EmptyState } from "./states";

/** Which product a search served. An unknown value renders verbatim rather than
 *  being coerced into one of the two we know: the backend owns this vocabulary
 *  and may grow it. */
function surfaceLabel(surface: string): string {
  if (surface === "funnel") return "Self-serve search";
  if (surface === "concierge") return "Concierge";
  return surface || "Search";
}

/** The column must agree with the order it sits next to. `rankTotal` IS the shortlist's own
 *  ordering key, raw, so rank 1 always reads highest and the numbers mean the same thing from
 *  one search to the next: the fit tier's points (2005 EXACT / 2000 STRONG / 1000 CORE_FIT /
 *  500 ADJACENT / 0 OFF) plus quality, minus the shortfall penalty. It is NOT rescaled 0-100
 *  across the shortlist, which is what shipped first and made the bottom pick read exactly 0
 *  however good it was: a STRONG seller at 2000 is not a zero.
 *
 *  `pickScore` (stored 0..1) is a different composition — quality normalized against the pool,
 *  miss penalty capped — that disagreed with the rank on 40% of searches; it is kept only as a
 *  fallback for rows written before `rankTotal`, and printed 0-100 as the Searches page does.
 *  The two are told apart by their scale and by the column's title. */
function scoreLabel(
  rankTotal: number | null | undefined,
  pickScore: number | null | undefined,
): string {
  if (rankTotal != null)
    return Number.isInteger(rankTotal) ? String(rankTotal) : rankTotal.toFixed(1);
  return pickScore == null ? "-" : (pickScore * 100).toFixed(0);
}

function PickRow({ pick }: { pick: AdminSearchPick }) {
  return (
    <tr>
      <td className="rc-num">{pick.rank}</td>
      <td>
        <div className="ex-seller-cell">
          <Link
            to="/explorer/talents/$sellerId"
            params={{ sellerId: pick.sellerId }}
            className="ex-primary-link"
          >
            {pick.sellerId}
          </Link>
          {/* SCOUT ranks up to 12 and the surface uses a subset, so saying which
              were used is how this shows a 12-pick shortlist without implying all
              twelve reached the client. Three rungs, strongest first: Picked (the
              client pressed this talent — a handoff row), Shown (rendered as a
              card, including a reserve the client revealed with "Show more"),
              Reserve (ranked, never client-visible).

              Picked is a SOLID BLUE pill, deliberately unlike its two siblings:
              the accent tone is the success tone in this palette, so a soft
              accent "Picked" was the same tint as the green "Shown" beside it
              and the strongest thing that can happen to a pick was the easiest
              to miss. Same pill as the outreach table above, so one glance reads
              both blocks. */}
          {pick.picked ? (
            <Badge
              tone="info"
              variant="solid"
              size="sm"
              title="The client picked this talent: pressed their card or contacted them"
            >
              Picked
            </Badge>
          ) : pick.contacted ? (
            <Badge tone="success" size="sm">
              Shown
            </Badge>
          ) : (
            <Badge tone="neutral" size="sm" title="Ranked, held in reserve">
              Reserve
            </Badge>
          )}
        </div>
        {pick.coreProfession ? (
          <span className="ex-cell-sub-wrap">{pick.coreProfession}</span>
        ) : null}
      </td>
      <td>
        {pick.fitCategory ? (
          <Badge tone="neutral" size="sm">
            {pick.fitCategory}
          </Badge>
        ) : (
          <span className="ex-muted">Unknown</span>
        )}
        {pick.relaxed.length ? (
          <span className="ex-cell-sub-wrap" title="Stated requirements this pick does not meet">
            relaxed: {pick.relaxed.join(", ")}
          </span>
        ) : null}
      </td>
      <td
        className="rc-num"
        title={
          pick.rankTotal != null
            ? "SCOUT's own ordering key: fit tier (2005 exact / 2000 strong / 1000 core / 500 adjacent) plus quality points, minus the shortfall penalty"
            : "This talent's combined fit and quality, 0-100 (legacy score: may not match the rank)"
        }
      >
        {scoreLabel(pick.rankTotal, pick.pickScore)}
      </td>
      <td className="ex-pick-reason">
        {/* SCOUT's verbatim output. An operator asking "why this talent" needs
            what the model actually said, so nothing here is rephrased. */}
        {pick.rationale || pick.fitRationale || (
          <span className="ex-muted">No reasoning recorded</span>
        )}
      </td>
      {/* The client's own Fiverr conversation with THIS talent, in Fiverr OS
          Chats Research: the same link (and the same pill) the outreach table
          above carries per thread, so a talent the client picked off a search
          card is one click from what the marketplace recorded, exactly like one
          Mira approached. Built backend-side from the two NUMERIC ids; a plain
          hyphen, not a disabled control, when either is unknown, because there
          is nothing to open and a dead button invites the click anyway. */}
      <td>
        {pick.fiverrOsChatUrl ? (
          <a
            className="ex-os-chat-link"
            href={pick.fiverrOsChatUrl}
            target="_blank"
            rel="noreferrer noopener"
            title={`Open the client's Fiverr conversation with ${pick.sellerId} in Fiverr OS`}
          >
            Fiverr OS
          </a>
        ) : (
          <span className="ex-muted">-</span>
        )}
      </td>
    </tr>
  );
}

/** The attrition ladder under the count line. Null text (a pre-0152 row with no
 *  ladder) renders nothing rather than an empty paragraph holding vertical space. */
function FunnelLadder({ funnel }: { funnel?: Record<string, number> }) {
  const text = funnelLadderText(funnel);
  if (!text) return null;
  return <p className="ex-muted ex-search-card__funnel">{text}</p>;
}

function SearchCard({
  search,
  canOpenSearch,
}: {
  search: ProjectScoutSearch;
  canOpenSearch: boolean;
}) {
  return (
    <section className="ex-search-card">
      <header className="ex-search-card__head">
        <Badge tone={search.surface === "concierge" ? "accent" : "info"} size="sm">
          {surfaceLabel(search.surface)}
        </Badge>
        <span className="ex-muted">{formatDateTime(search.createdAt)}</span>
        <span className="rc-cell-sub">
          {search.rankedCount} ranked of {search.poolCount.toLocaleString()} found
        </span>
        {/* An upstream that answers with sellers but no gig data is the ONE
            attrition seam an operator cannot infer from any other number here,
            and it is the one that produced a 0-pool hunt on a healthy brief. It
            gets its own badge; the rest of the ladder reads below. */}
        {missingGigData(search.funnel) > 0 ? (
          <Badge
            tone="warning"
            size="sm"
            title="The marketplace returned these sellers with no priced package, so SCOUT could not judge scope or budget for them. An upstream data gap, not a thin marketplace."
          >
            {missingGigData(search.funnel).toLocaleString()} missing gig data
          </Badge>
        ) : null}
        {search.degraded ? (
          <Badge
            tone="warning"
            size="sm"
            title="SCOUT finished on a degraded path, so this shortlist may be thinner than usual."
          >
            Degraded
          </Badge>
        ) : null}
        {/* The forensic view lives on the Talent console. Offered only to a
            viewer who actually holds that area: an `explorer`-only operator
            would land on the access-required panel, and a dead-end link is
            worse than no link. */}
        {canOpenSearch ? (
          <Link
            to="/talent-management/searches/$searchId"
            params={{ searchId: search.id }}
            className="ex-primary-link ex-search-card__more"
          >
            Full search
          </Link>
        ) : null}
      </header>

      <FunnelLadder funnel={search.funnel} />

      {search.rationale ? <p className="ex-prose">{search.rationale}</p> : null}

      {search.picks.length === 0 ? (
        // A search that ranked NOBODY is a real and interesting outcome, not a
        // rendering gap. Saying so beats an empty table. WHY it ranked nobody is
        // the ladder above, which is the part that used to be missing.
        <p className="ex-muted">SCOUT ranked nobody on this search.</p>
      ) : (
        <div className="rc-table-wrap">
          <table className="rc-table">
            <TableExport name="search-picks" label="SCOUT picks" />
            <thead>
              <tr>
                <th>#</th>
                <th>Talent</th>
                <th>Tier</th>
                <th>Total</th>
                <th>Why SCOUT picked them</th>
                <th>Fiverr OS</th>
              </tr>
            </thead>
            <tbody>
              {[...search.picks]
                .sort((a, b) => a.rank - b.rank)
                .map((pick) => (
                  <PickRow key={`${pick.rank}-${pick.sellerId}`} pick={pick} />
                ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ProjectSearchPicks({
  searches,
  total,
  /** True when the project also has concierge outreach above this block. Only
   *  changes the heading: with threads present these are "the rest of the
   *  shortlist", without them they are the whole story. */
  hasThreads,
}: {
  searches: readonly ProjectScoutSearch[];
  total: number;
  hasThreads: boolean;
}) {
  const areas = useQuery(myAreasQuery);
  // A READ: the linked search detail is `talent_management` at `read`, so a
  // read-only analyst follows it like anyone else.
  const canOpenSearch = hasArea(areas.data ?? [], "talent_management");

  if (searches.length === 0) {
    if (hasThreads) return null;
    return (
      <EmptyState
        title="No talent found yet"
        message="No search has run for this project, so nobody has been shortlisted or approached."
      />
    );
  }

  return (
    <div className="ex-stack">
      <div className="ex-search-picks__head">
        <h3 className="ad-card__title">
          {hasThreads ? "Shortlists SCOUT ranked" : "Talent SCOUT found"}
        </h3>
        {/* The list is bounded by the backend. Saying "latest N of M" is the
            difference between a truncated view and one that looks complete. */}
        {total > searches.length ? (
          <span className="rc-cell-sub">
            latest {searches.length} of {total} searches
          </span>
        ) : null}
      </div>
      {searches.map((search) => (
        <SearchCard key={search.id} search={search} canOpenSearch={canOpenSearch} />
      ))}
    </div>
  );
}
